<script setup lang="ts">
import { useTemplateRef, watchEffect } from "vue";
import { UiText, useSharedLink, type ShopexpertsCrawlerProfilePayload, type ShopexpertsPreviewExpert } from "@shopexperts/shared";
import type z from "zod";
import type { OverallReviewsSchema } from "@shopexperts/shared";

type OverallReviews = z.infer<typeof OverallReviewsSchema>;

// The crawler preview renders on the public `/partner-preview` page. Use the
// admin-specific preview env when present, then fall back to the shared profile
// preview env used by the freelancer preview. Only the origin is used; the
// partner preview path is forced below.
const PREVIEW_ENV_URL =
  import.meta.env.VITE_ADMIN_PROFILE_PREVIEW_URL ||
  import.meta.env.VITE_PROFILE_PREVIEW_URL ||
  "http://localhost:3000/partner-preview";
const PROFILE_PREVIEW_ORIGIN = new URL(PREVIEW_ENV_URL, window.location.origin).origin;
const PROFILE_PREVIEW_URL = new URL("/partner-preview", PROFILE_PREVIEW_ORIGIN).toString();

const previewIframe = useTemplateRef<HTMLIFrameElement>("previewIframe");

const props = defineProps<{
  body: ShopexpertsPreviewExpert;
  reviews: OverallReviews;
  crawlerProfile: ShopexpertsCrawlerProfilePayload;
}>();

const { data: profile } = useSharedLink<ShopexpertsPreviewExpert>({
  iframeRef: previewIframe,
  name: "shopexperts-service",
  mode: "providerToClient",
  targetOrigin: PROFILE_PREVIEW_ORIGIN,
});

const { data: reviews } = useSharedLink<OverallReviews>({
  iframeRef: previewIframe,
  name: "overall-reviews",
  mode: "providerToClient",
  targetOrigin: PROFILE_PREVIEW_ORIGIN,
});

const { data: crawlerProfile } = useSharedLink<ShopexpertsCrawlerProfilePayload>({
  iframeRef: previewIframe,
  name: "shopexperts-crawler-profile",
  mode: "providerToClient",
  targetOrigin: PROFILE_PREVIEW_ORIGIN,
});

watchEffect(() => {
  profile.value = props.body;
});

watchEffect(() => {
  reviews.value = props.reviews;
});

watchEffect(() => {
  crawlerProfile.value = props.crawlerProfile;
});
</script>

<template>
  <div class="flex h-full w-full flex-col">
    <div class="w-full flex-shrink-0 bg-se-dark p-1 text-center font-semibold text-se-white">
      <UiText as="h6" text="This is a preview of how this expert could look on shopexperts, built from the mapped data. This is not a live listing." />
    </div>
    <iframe ref="previewIframe" :src="PROFILE_PREVIEW_URL" class="w-full flex-1" />
  </div>
</template>
